// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { ContextualMenuItemType, IContextualMenuProps, IContextualMenuItem, Toggle } from '@fluentui/react';
import { LightBorderButton, headerLightButtonStyles } from '../components/LightBorderButton';
import { Experiment, useExperiments } from './ExperimentsProvider';

export const ExperimentsButton = (): JSX.Element => {
  const { experimentValues, updateExperimentValue } = useExperiments();

  const experimentsAsMenuItems: IContextualMenuItem[] = Object.keys(experimentValues).map((key) => {
    const experiment: Experiment<boolean> = experimentValues[key];
    return {
      key: experiment.name,
      onRender: () => {
        return (
          <BooleanExperimentControl
            name={experiment.name}
            value={experiment.userValue ?? experiment.defaultValue}
            onChange={(checked) => updateExperimentValue(key as keyof typeof experimentValues, checked)}
          />
        );
      }
    };
  });

  const menuProps: IContextualMenuProps = {
    items: [
      ...experimentsAsMenuItems,
      { key: 'divider_1', itemType: ContextualMenuItemType.Divider },
      {
        key: 'reset',
        text: 'Reset to defaults',
        onClick: (e) => {
          for (const key in experimentValues) {
            updateExperimentValue(key as keyof typeof experimentValues, undefined);
          }
          e?.preventDefault();
        }
      }
    ],
    styles: {
      root: { margin: '0.5rem' }
    },
    directionalHintFixed: true
  };

  return (
    <LightBorderButton
      menuProps={menuProps}
      styles={headerLightButtonStyles}
      text="Experiments"
      iconProps={{ iconName: 'Experiments' }}
    />
  );
};

const BooleanExperimentControl = (props: {
  name: string;
  value: boolean;
  onChange: (newValue: boolean | undefined) => void;
}): JSX.Element => {
  return (
    <Toggle
      label={props.name}
      inlineLabel
      checked={props.value}
      onChange={(_, checked) => {
        props.onChange(checked);
      }}
    />
  );
};
